import {
  Alert,
  AlertDescription,
  Text,
  UseDisclosureReturn,
  VStack,
} from "@chakra-ui/react";
import {
  AccountSubType,
  AccountType,
} from "@syla/shared/types/models/AccountBase";
import React from "react";
import { useForm } from "react-hook-form";
import { useSwitchAccount } from "../../../routers/useSwitchAccount";
import { useMutationCreateAccount } from "../../../store/useMutationCreateAccount";
import { InputField } from "../../forms/InputField";
import { SingleSelectorField } from "../../forms/SingleSelectorField";
import { FormModal } from "../../modals/FormModal";
import {
  ACCOUNT_TYPE_OPTIONS,
  ACTIVITY_TYPE_OPTIONS,
} from "../../molecules/forms/accountOptions";

type CreateAccountFormValues = {
  accountType: AccountType;
  accountSubType: AccountSubType;
  fullName: string;
  nickname?: string;
};

export function CreateAccountModal({
  disclosure,
}: {
  disclosure: UseDisclosureReturn;
}): React.ReactElement {
  const switchAccount = useSwitchAccount();

  const form = useForm<CreateAccountFormValues>({
    defaultValues: {
      accountType: AccountType.Individual,
      accountSubType: AccountSubType.Investor,
      fullName: "",
      nickname: "",
    },
    mode: "onTouched",
  });

  const mutationCreateAccount = useMutationCreateAccount();

  const onSubmit = async (data: CreateAccountFormValues) => {
    const { accountId } = await mutationCreateAccount.mutateAsync({
      accountType: data.accountType,
      accountSubType: data.accountSubType,
      nickname: data.nickname,
      fullName: data.fullName,
    });

    switchAccount({
      _id: accountId,
      fullName: data.fullName,
      nickname: data.nickname,
    });
  };

  return (
    <FormModal
      disclosure={disclosure}
      form={form}
      title="Create new account"
      submitButtonText="Create account"
      errorTitle="Create account failed"
      onSubmit={onSubmit}
    >
      <Text color="gray.700" mb={6}>
        Add a new account for yourself, a client, company, trust, or SMSF.
      </Text>

      <Alert status="info" borderRadius="md" mb={6}>
        <AlertDescription color="gray.700">
          New accounts start on the Free plan and can be upgraded to a paid
          subscription to unlock tax reporting.
        </AlertDescription>
      </Alert>
      <VStack width="full" spacing={4}>
        <SingleSelectorField
          form={form}
          name="accountType"
          label="Account type"
          helperText="Choose who or what this tax account is for."
          isRequired
          options={ACCOUNT_TYPE_OPTIONS}
          placeholder="Select an account type"
          buttonProps={{
            width: "full",
          }}
          popoverProps={{
            matchWidth: true,
          }}
        />

        <SingleSelectorField
          form={form}
          name="accountSubType"
          label="Activity type"
          helperText="Choose the main reason this account holds or trades crypto."
          isRequired
          options={ACTIVITY_TYPE_OPTIONS}
          placeholder="Select an activity type"
          buttonProps={{
            width: "full",
          }}
          popoverProps={{
            matchWidth: true,
          }}
        />

        <InputField
          form={form}
          name="fullName"
          label="Name"
          placeholder="Satoshi Nakamoto"
          helperText="Use the legal name or entity name for this account."
          isRequired
          autoFocus
        />

        <InputField
          form={form}
          name="nickname"
          label="Nickname"
          placeholder="SN01"
          helperText="An optional nickname or reference you use for this account."
        />
      </VStack>
    </FormModal>
  );
}
